<template>
    <v-container fluid>
        <h4>Neue Sprachnachricht</h4>
        <v-form ref="form"
                v-model="valid">
            <v-autocomplete
                    :rules="deviceRules"
                    v-model="device"
                    :items="sensors"
                    color="blue-grey lighten-2"
                    label="Smart Sensor"
                    item-text="environment.label"
                    item-value="device"
                    required>
            </v-autocomplete>
            <div class="caru-recorder">
                <v-u-meter :avgMicFrequency="avgMicFrequency" :enablePulse="state === 'recording'"></v-u-meter>
                <v-btn @click="recordHandle" class="caru-recorder__btn mt-0"
                       :color="state === 'recording' ? 'success' : 'error' ">{{recordButtonLabel}}
                </v-btn>
                <div class="caru-recorder__player" v-if="audioSrc && state === 'done'">
                    <audio controls="controls">
                        Your browser does not support the <code>audio</code> element.
                        <source v-bind:src="audioSrc">
                    </audio>
                </div>
            </div>
            <v-btn color="success" :loading="messageSaving" :disabled="!formValid || messageSaving"
                   @click="saveMessage">
                Abschicken
            </v-btn>
        </v-form>
        <v-dialog v-model="rerecordConfirmDialog" persistent max-width="290">
            <v-card>
                <v-card-title class="headline">Aufnahme überspielen?</v-card-title>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="green darken-1" flat="flat" @click="rerecordConfirmDialog = false">
                        Nein
                    </v-btn>
                    <v-btn color="green darken-1" flat="flat" @click="startRecording">
                        Ja
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import Recorder from '../recoder/recorder'
import VUMeter from '../components/VuMeter'
import API, { graphqlOperation } from '@aws-amplify/api'
import getWavLength from '../recoder/wav'

import prepareQuery from '../prepare.graphql'
import saveMutation from '../save.graphql'

import { uuid4 } from '../../common/uuid'

const {mapActions, mapState} = createNamespacedHelpers('sensor')

const State = {
  READY: 'ready',
  RECORDING: 'recording',
  DONE: 'done'
}

export default {
  name: 'VoiceMessageOverview',
  components: {VUMeter},
  data () {
    return {
      valid: false,
      device: null,
      deviceRules: [
        v => !!v || 'Smart Sensor auswählen',
      ],
      recordButtonLabel: 'Aufnahme',
      audioSrc: null,
      avgMicFrequency: null,
      state: State.READY,
      animateVUMeter: null,
      recorder: new Recorder(),
      recording: null,
      rerecordConfirmDialog: false,
      messageSaving: false
    }
  },
  computed: {
    formValid () {
      return this.valid && this.recording !== null
    },
    ...mapState({
      sensors: state => state.sensors,
    })
  },
  async created () {
    await this.getSensors()
    this.$store.commit('core/setViewLoading', false)
  },
  methods: {
    reset () {
      this.state = State.READY
      this.recording = null
      this.$refs.form.reset()
    },
    async saveMessage () {
      this.messageSaving = true
      // Create file name
      const recodingId = uuid4()
      const recodingName = `${recodingId}.wav`

      // Get signed post URL
      const sv = await API.graphql(graphqlOperation(prepareQuery))
      const requestConfig = JSON.parse(sv.data.prepareVoiceMessage)

      let formData = new FormData()
      Object.keys(requestConfig['fields']).forEach(key => {
        formData.append(key, requestConfig.fields[key])
      })

      formData.append('file', this.recording.blob, recodingName)

      await fetch(requestConfig.url, {
        method: 'POST',
        mode: 'no-cors',
        body: formData
      })

      let start = new Date()
      let end = new Date(start.getTime() + 1000 * 60 * 60 * 24)
      await API.graphql(graphqlOperation(saveMutation, {
        deviceIds: [this.device],
        recordingId: recodingId,
        messageDuration: Math.round(this.recording.duration),
        startDate: start.toISOString(),
        endDate: end.toISOString(),
      }))

      this.messageSaving = false
      this.$store.commit('core/snackBarShow', {text: 'Sprachnachricht verschickt.'})
      this.reset()

    },
    updateMicFrequency () {
      // Stop updating if no longer recording
      if (this.state !== State.RECORDING) {
        window.cancelAnimationFrame(this.animateVUMeter)
        return
      }
      // Grab average microphone frequency
      this.avgMicFrequency = this.recorder.getAverageMicFrequency()

      // Throttle updating slightly
      setTimeout(() => {
        this.animateVUMeter = window.requestAnimationFrame(() => {
          this.updateMicFrequency()
        })
      }, 10)
    },
    async startRecording () {
      this.rerecordConfirmDialog = false
      this.audioSrc = ''
      await this.recorder.start()
      this.state = State.RECORDING
      this.updateMicFrequency()
      this.recordButtonLabel = 'Stop'
    },
    async recordHandle () {
      if (this.state === State.READY) {
        this.startRecording()
      } else if (this.state === State.RECORDING) {
        this.recordButtonLabel = 'Aufnahme'
        this.state = State.DONE
        this.recorder.stop()
        let blob = await this.recorder.getWav()
        let duration = await getWavLength(blob)

        this.recording = {
          blob,
          duration
        }

        this.recorder.releaseMic()
        const url = URL.createObjectURL(blob)
        this.audioSrc = url
      } else if (this.state === State.DONE) {
        this.rerecordConfirmDialog = true
      }
    },
    stop () {
      this.state = State.DONE

    },
    ...mapActions([
      'getSensors',
      'getSensorValues'
    ])
  }
}
</script>

<style lang="scss">
    .caru-recorder {
        width: 300px;
        margin: 0 auto;

        &__player {
            margin-top: 40px;
            height: 80px;

        }

        &__btn {
            display: block;
            margin: 8px auto;
        }
    }
</style>
